import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";
import { ListScaffold } from "@/components/layout/ListScaffold";
import {
  Badge,
  Button,
  PageHeader,
  Select,
  Table,
  TableWrap,
  TD,
  TH,
  THead,
  TR,
} from "@/components/ui";
import { useApi } from "@/lib/api";
import { ago, personLabel } from "@/lib/format";

type Status = "pending" | "accepted" | "rejected";

/**
 * Cafes somebody asked us to add.
 *
 * Accepting creates a real cafe row from the proposal and links the two, in
 * one transaction — so a half-accepted proposal, which would put the same
 * venue in the queue twice, cannot exist. The new cafe is marked
 * `user_proposed`, not `admin`: it came from somebody who went there, and
 * that provenance is what the crawl queue reads later.
 */
export function ProposalsScreen() {
  const api = useApi();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<Status>("pending");

  const proposals = useQuery({
    queryKey: ["admin", "proposals", status],
    queryFn: async () => {
      const { data, error } = await api.GET("/admin/cafes/proposals", {
        params: { query: { status, limit: 50 } },
      });
      if (error) throw error;
      return data;
    },
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ["admin", "proposals"] });
    void queryClient.invalidateQueries({
      queryKey: ["admin", "reports", "counts"],
    });
    void queryClient.invalidateQueries({ queryKey: ["admin", "dashboard"] });
  };

  const accept = useMutation({
    mutationFn: async (proposalId: string) => {
      const { error } = await api.POST(
        "/admin/cafes/proposals/{proposalId}/accept",
        { params: { path: { proposalId } } },
      );
      if (error) throw error;
    },
    onSuccess: invalidate,
  });

  const reject = useMutation({
    mutationFn: async (proposalId: string) => {
      const { error } = await api.POST(
        "/admin/cafes/proposals/{proposalId}/reject",
        { params: { path: { proposalId } }, body: {} },
      );
      if (error) throw error;
    },
    onSuccess: invalidate,
  });

  const busy = accept.isPending || reject.isPending;

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        eyebrow="Queues"
        title="Cafe proposals"
        description="Accepting adds the venue to the map immediately, as user-proposed. Nothing is crawled or rated until somebody reviews it."
      />
      <ListScaffold
        filters={
          <Select
            value={status}
            onChange={(e) => setStatus(e.target.value as Status)}
          >
            <option value="pending">Pending</option>
            <option value="accepted">Accepted</option>
            <option value="rejected">Rejected</option>
          </Select>
        }
        isLoading={proposals.isLoading}
        isError={proposals.isError}
        onRetry={() => void proposals.refetch()}
        isEmpty={(proposals.data?.items.length ?? 0) === 0}
        emptyTitle={status === "pending" ? "Queue is clear" : "Nothing here"}
        emptyHint={
          status === "pending"
            ? "Proposals arrive from the app when somebody finds a place we do not have."
            : undefined
        }
      >
        <TableWrap>
          <Table>
            <THead>
              <TR>
                <TH>Proposed cafe</TH>
                <TH>Where</TH>
                <TH>Note</TH>
                <TH>By</TH>
                <TH>Asked</TH>
                <TH align="right" />
              </TR>
            </THead>
            <tbody>
              {proposals.data?.items.map((proposal) => (
                <TR key={proposal.id}>
                  <TD>
                    <span className="font-medium text-ink">
                      {proposal.name}
                    </span>
                    {proposal.website ? (
                      <a
                        href={proposal.website}
                        target="_blank"
                        rel="noreferrer noopener"
                        className="ml-1.5 text-micro text-accent hover:underline"
                        onClick={(e) => e.stopPropagation()}
                      >
                        site
                      </a>
                    ) : null}
                  </TD>
                  <TD muted>
                    {[proposal.street, proposal.housenumber]
                      .filter(Boolean)
                      .join(" ") || "—"}
                    <span className="text-muted-2">
                      {" "}
                      · {proposal.city ?? "?"}
                    </span>
                  </TD>
                  <TD muted className="max-w-[280px]">
                    <span className="block truncate">
                      {proposal.note ?? "—"}
                    </span>
                  </TD>
                  <TD>{personLabel(proposal.user)}</TD>
                  <TD muted>{ago(proposal.createdAt)}</TD>
                  <TD align="right">
                    {proposal.status === "pending" ? (
                      <span className="flex justify-end gap-1.5">
                        <Button
                          variant="primary"
                          disabled={busy}
                          onClick={() => accept.mutate(proposal.id)}
                        >
                          Add to the map
                        </Button>
                        <Button
                          variant="danger"
                          disabled={busy}
                          onClick={() => reject.mutate(proposal.id)}
                        >
                          Reject
                        </Button>
                      </span>
                    ) : proposal.cafeId ? (
                      <Link
                        to={`/cafes/${proposal.cafeId}`}
                        className="text-micro text-accent hover:underline"
                      >
                        open the cafe
                      </Link>
                    ) : (
                      <Badge tone="neutral">{proposal.status}</Badge>
                    )}
                  </TD>
                </TR>
              ))}
            </tbody>
          </Table>
        </TableWrap>
      </ListScaffold>
    </div>
  );
}
